import {
  AnimatedList,
  AnimatedListItem,
} from "@babackapp/customer-ui/components/animated-list";
import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import { ImageInput } from "@babackapp/customer-ui/components/image-input";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  InputGroupTextarea,
} from "@babackapp/customer-ui/components/input-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { EnterIcon } from "@radix-ui/react-icons";
import { AlertCircleIcon, ArrowLeft, MessageCircleMoreIcon } from "lucide-react";
import { AnimatePresence } from "motion/react";
import { useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { useLineItemReasonForm } from "../../hooks/use-line-item-reason-form";
import { useCustomText } from "@babackapp/return-widget-components/hooks/use-custom-text";
import { useRemoveLineItem } from "@babackapp/return-widget-components/hooks/use-remove-line-item";
import { useWidgetParams } from "../../hooks/use-widget-params";
import { useBrowserBack } from "../../hooks/use-browser-back";
import { UserPortalCallout } from "@babackapp/return-widget-components/components/user-portal-callout";
import { ReasonOptionSelector } from "./reason-option-selector";

export function Reason() {
  const { form, onSubmit, reasons, selectedReason, userPortalTexts } = useLineItemReasonForm();
  const navigate = useNavigate();
  const { lineItemId = "" } = useParams<{ lineItemId: string }>();
  const { orderId } = useWidgetParams();
  const removeLineItem = useRemoveLineItem(orderId);
  const [isSelectorOpen, setIsSelectorOpen] = useState(true);

  const handleBack = () => {
    removeLineItem(lineItemId);
    navigate("../../");
  };

  useBrowserBack(handleBack);
  const t = useCustomText();

  const showComment = !selectedReason || selectedReason.commentAvailable;
  const showImages = !selectedReason || selectedReason.photoAvailable;

  const errorMessages = [
    form.formState.errors.reasonId?.message,
    form.formState.errors.comment?.message,
    form.formState.errors.images?.message,
  ].filter(Boolean) as string[];

  return (
    <WidgetPage>
      <Header>
        <HeaderTitle>{t("reason.title")}</HeaderTitle>
        <HeaderDescription>
          {t("reason.description")}
        </HeaderDescription>
      </Header>
      <UserPortalCallout texts={userPortalTexts} />

      {errorMessages.length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            {errorMessages.map((msg, i) => <div key={i}>{msg}</div>)}
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-1 flex-col min-h-0" onSubmit={onSubmit}>
        <div className="relative flex flex-1 flex-col min-h-0">
          <div className="flex flex-1 min-h-24 flex-col overflow-y-auto px-1 pb-20">
          <AnimatedList>
            <AnimatedListItem>
              <Controller
                control={form.control}
                name="reasonId"
                render={({ field }) => (
                  <ReasonOptionSelector
                    value={field.value ?? ""}
                    options={reasons.map((r) => ({
                      id: r.id,
                      label: r.label,
                      ...(r.isRetractation ? { gapBefore: 20 } : {}),
                    }))}
                    onChange={field.onChange}
                    onOpenChange={setIsSelectorOpen}
                  />
                )}
              />
            </AnimatedListItem>

            <AnimatePresence>
              {showComment && (
                <AnimatedListItem key="comment">
                  <Controller
                    control={form.control}
                    name="comment"
                    render={({ field }) => (
                      <InputGroup className="h-auto">
                        <InputGroupTextarea
                          placeholder={
                            selectedReason?.requiresComment
                              ? t("reason.commentRequired")
                              : t("reason.commentOptional")
                          }
                          value={field.value ?? ""}
                          onChange={(e) => field.onChange(e.target.value || null)}
                          aria-invalid={!!form.formState.errors.comment}
                        />
                        <InputGroupAddon align="block-start">
                          <InputGroupText>{t("reason.commentLabel")}</InputGroupText>
                          <MessageCircleMoreIcon />
                        </InputGroupAddon>
                      </InputGroup>
                    )}
                  />
                </AnimatedListItem>
              )}
            </AnimatePresence>

            <AnimatePresence>
              {showImages && (
                <AnimatedListItem key="images">
                  <Controller
                    control={form.control}
                    name="images"
                    render={({ field }) => (
                      <ImageInput onChange={field.onChange} />
                    )}
                  />
                </AnimatedListItem>
              )}
            </AnimatePresence>
          </AnimatedList>
          </div>
          <div className="pointer-events-none absolute bottom-0 inset-x-0 h-12 bg-gradient-to-t from-background to-transparent" />
        </div>

        <AnimatedButtonRow exit={{}}>
          <AnimatedButtonContainer
            layoutId="line-item-back-button"
            disableVariants={{ exit: true }}
          >
            <Button variant="secondary" type="button" onClick={handleBack}>
              <ArrowLeft />
              {t("common.back")}
            </Button>
          </AnimatedButtonContainer>
          {!isSelectorOpen && (
            <AnimatedButtonContainer
              layoutId="line-item-confirm-button"
              disableVariants={{ exit: true }}
            >
              <Button type="submit">
                <EnterIcon />
                {t("common.confirm")}
              </Button>
            </AnimatedButtonContainer>
          )}
        </AnimatedButtonRow>
      </form>
    </WidgetPage>
  );
}
